import React, { useContext, useEffect, useState, useMemo } from "react";
import EligibilityFormatter from "./EligibilityFormatter";
import EligibilityReasonFormatter from "./EligibilityReasonFormatter";
import BirthDateFormatter from "../table/BirthDateFormatter";
import PlayerLinkFormatter from "../table/PlayerLinkFormatter";
import BirthCountryFormatter from "../table/BirthCountryFormatter";
import PlayerEligibilityContext from "../../contexts/PlayerEligibilityContext";
import WBCYearsContext from "../../contexts/WBCYearsContext";
import EligibilityListApi from "../../httpClients/EligibilityListApi";
import DocsFormatter from "../interest-list/DocsFormatter";
import CheckMarkFormatter from "../elements/CheckMarkFormatter";
import WBCDataTable from "../data-table/WBCDataTable";
import LocalDateTimeStringFormatter from "../table/LocalDateTimeStringFormatter";
import EligibilityReasonModal from "./EligibilityReasonModal";
import styled from "styled-components";

const PlayerEligibilityTableContainer = styled.div`
  height: 100%;
`;

const PlayerEligibilityTable = () => {
  // context(s)
  const { selectedYear } = useContext(WBCYearsContext);
  const playerEligibilityContext = useContext(PlayerEligibilityContext);

  // state for modal
  const [showEligibilityReasonModal, setShowEligibilityReasonModal] = useState(false);
  const [selectedPlayer, setSelectedPlayer] = useState(null);

  // variable(s)
  const {
    displayedPlayers,
    sortFilters,
    statusFilter,
    team: { teamId }
  } = playerEligibilityContext.state;
  const { dispatch: eligibilityDispatch } = playerEligibilityContext;

  // function to handle opening the modal
  const handleOpenEligibilityModal = player => {
    setSelectedPlayer(player);
    setShowEligibilityReasonModal(true);
  };

  // base columns definition
  const baseColumns = [
    {
      key: "submittedBy",
      name: "Submitted By",
      sortable: true,
      width: 150
    },
    {
      key: "docs",
      name: " ",
      sortable: true,
      width: 55,
      formatter: DocsFormatter
    },
    {
      key: "lastName",
      name: "Last Name",
      sortable: true,
      width: 120,
      formatter: PlayerLinkFormatter
    },
    {
      key: "firstName",
      name: "First Name",
      sortable: true,
      width: 120,
      formatter: PlayerLinkFormatter
    },
    {
      key: "rosterFirstName",
      name: "Preferred Name",
      sortable: true,
      width: 150,
      formatter: PlayerLinkFormatter
    },
    {
      key: "pastWbcFlag",
      name: "Past WBC",
      sortable: true,
      width: 90,
      formatter: CheckMarkFormatter
    },
    {
      key: "birthDate",
      name: "Birth Date",
      sortable: true,
      width: 100,
      formatter: BirthDateFormatter
    },
    {
      key: "birthCountryId",
      name: "Birth Country",
      sortable: true,
      width: 170,
      formatter: BirthCountryFormatter
    },
    {
      key: "eligibility",
      name: "Eligibility",
      sortable: true,
      width: 350,
      formatter: row => (
        <EligibilityFormatter
          value={row.row.eligibility}
          row={row.row}
          onOpenEligibilityModal={handleOpenEligibilityModal}
        />
      )
    },
    {
      key: "submittedEligibilityDate",
      name: "Submitted Date",
      sortable: true,
      formatter: LocalDateTimeStringFormatter
    }
  ];

  // Dynamic columns with eligibility reason column when viewing approved players
  const columns = useMemo(() => {
    if (statusFilter?.label === "Approved") {
      // Insert eligibility reason column before the last column (submittedEligibilityDate)
      const eligibilityReasonColumn = {
        key: "eligibilityReason",
        name: "Eligibility Reason",
        sortable: true,
        width: 200,
        formatter: row => <EligibilityReasonFormatter row={row.row} />
      };

      // Create new array with eligibility reason column inserted before the last column
      const newColumns = [...baseColumns];
      newColumns.splice(-1, 0, eligibilityReasonColumn);
      return newColumns;
    }

    return baseColumns;
  }, [statusFilter?.label, handleOpenEligibilityModal]);

  useEffect(() => {
    if (selectedYear && teamId !== undefined) {
      EligibilityListApi.getPlayerEligibilityList(selectedYear, teamId).then(response => {
        eligibilityDispatch({ type: "setPlayers", players: response });
      });
    }
  }, [teamId, selectedYear, eligibilityDispatch]);

  return (
    <PlayerEligibilityTableContainer>
      <WBCDataTable
        useBCR
        id={"player-eligibility-table"}
        data={displayedPlayers}
        columns={columns}
        minColumnWidth={270}
        sortFunc={(col, direction) =>
          eligibilityDispatch({
            type: "setSortFilters",
            sortFilters: { key: col, direction: direction }
          })
        }
        height={700}
        sortColumn={sortFilters.key}
        sortDirection={sortFilters.direction}
      />
      <EligibilityReasonModal
        isOpen={showEligibilityReasonModal}
        setIsOpen={setShowEligibilityReasonModal}
        selectedPlayer={selectedPlayer}
      />
    </PlayerEligibilityTableContainer>
  );
};

export default PlayerEligibilityTable;
